/**
 * Guard Location Types
 *
 * Type definitions for the guard location tracking feature.
 */

/** Guard status based on location recency and patrol status */
export type GuardStatus = 'active' | 'stale' | 'offline' | 'not_on_patrol';

/** Patrol status for current assignment */
export type PatrolStatus = 'pending' | 'in_progress' | 'completed';

/** Site coordinates for patrol location mapping */
export interface SiteCoordinates {
  latitude: number;
  longitude: number;
}

/** Current patrol information */
export interface CurrentPatrol {
  patrolId: number;
  customerId: number;
  customerName: string;
  siteId: number;
  siteName: string;
  startTime: string;
  // Enhanced patrol assignment data (Issue 4.2)
  patrolStatus?: PatrolStatus;
  startedAt?: string | null;
  checkpointsCompleted?: number;
  checkpointsTotal?: number;
  // Site location for patrol circles on map
  addressId?: number | null;
  siteCoordinates?: SiteCoordinates | null;
}

/** Raw guard location data from API */
export interface GuardLocationRaw {
  guardId: number;
  name: string;
  latitude: number;
  longitude: number;
  timestamp: string;
  minutesAgo: number;
  accuracy: number | null;
  batteryPercent: number | null;
  deviceInfo: string | null;
  currentPatrol: CurrentPatrol | null;
}

/** Transformed guard data with derived properties */
export interface GuardLocation extends GuardLocationRaw {
  status: GuardStatus;
  lastSeenFormatted: string;
  coordinates: { lat: number; lng: number };
}

/** API response for guard locations */
export interface GuardLocationsResponse {
  guards: GuardLocationRaw[];
  timestamp: string;
  count: number;
}

/** Filter state for guard location panel */
export interface GuardLocationFilters {
  showActive: boolean;
  showStale: boolean;
  showOffline: boolean;
  showNotOnPatrol: boolean;
  searchQuery: string;
  customerId: number | null;
}

/** Sort options for guard list */
export type GuardSortOption = 'name' | 'lastSeen' | 'status';

/** Status color mapping - values should match CSS variables in styles/index.css */
export const STATUS_COLORS: Record<GuardStatus, string> = {
  active: '#4CAF50',      // --cl-success-light - on patrol, recent update
  stale: '#FFC107',       // --cl-warning-light - on patrol, stale update
  offline: '#9E9E9E',     // --cl-grey-500 - no recent update
  not_on_patrol: '#64b5f6', // --cl-sds-main - has location but not on patrol
};

/** Status display labels */
export const STATUS_LABELS: Record<GuardStatus, string> = {
  active: 'Active',
  stale: 'Stale',
  offline: 'Offline',
  not_on_patrol: 'Not on Patrol',
};

/** Patrol status colors - values should match CSS variables in styles/index.css */
export const PATROL_STATUS_COLORS: Record<PatrolStatus, string> = {
  pending: '#9E9E9E',      // --cl-grey-500 - scheduled but not started
  in_progress: '#64b5f6',  // --cl-sds-main - currently active
  completed: '#4CAF50',    // --cl-success-light - finished
};

/** Patrol status labels */
export const PATROL_STATUS_LABELS: Record<PatrolStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
};

/** Patrol status icons (unicode/emoji) */
export const PATROL_STATUS_ICONS: Record<PatrolStatus, string> = {
  pending: '⏱',
  in_progress: '▶',
  completed: '✓',
};

// ============================================================================
// Clustering Types (Issue 7.1)
// ============================================================================

import type Supercluster from 'supercluster';

/** Properties attached to each guard point for supercluster */
export interface GuardPointProperties {
  guardId: number;
  name: string;
  status: GuardStatus;
  batteryPercent: number | null;
  hasAlert: boolean; // Derived: stale/offline OR battery < 20%
}

/** A single guard point for supercluster (GeoJSON Feature) */
export type GuardPoint = Supercluster.PointFeature<GuardPointProperties>;

/** A cluster returned by supercluster */
export type GuardCluster = Supercluster.ClusterFeature<GuardPointProperties>;

/** Either a cluster or a single point */
export type ClusterOrPoint = GuardCluster | GuardPoint;

/** Map bounds for clustering calculations */
export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/** Type guard to check if a feature is a cluster */
export function isCluster(feature: ClusterOrPoint): feature is GuardCluster {
  return 'cluster' in feature.properties && feature.properties.cluster === true;
}

// ============================================================================
// Trail Types (Issue 7.2)
// ============================================================================

/** A single point in the location history trail */
export interface LocationHistoryPoint {
  locationId: number;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  recordedAt: string;
  batteryPercent: number | null;
}

/** Response from guard location history API */
export interface LocationHistoryResponse {
  contactId: number;
  locations: LocationHistoryPoint[];
  count: number;
}

/** Trail styling options */
export interface TrailOptions {
  /** Stroke color for the trail line (default: blue) */
  strokeColor?: string;
  /** Stroke opacity 0-1 (default: 0.8) */
  strokeOpacity?: number;
  /** Stroke weight in pixels (default: 3) */
  strokeWeight?: number;
  /** Show direction arrows (default: true) */
  showArrows?: boolean;
}

/** Default trail colors matching guard status - values should match CSS variables */
export const TRAIL_COLORS: Record<GuardStatus, string> = {
  active: '#4CAF50',      // --cl-success-light
  stale: '#FFC107',       // --cl-warning-light
  offline: '#9E9E9E',     // --cl-grey-500
  not_on_patrol: '#64b5f6', // --cl-sds-main
};

// ============================================================================
// Patrol Circle Types (Patrol Location Markers on Map)
// ============================================================================

/** Patrol circle status based on guard proximity */
export type PatrolCircleStatus =
  | 'guard_inside'   // Guard is within 50m radius
  | 'guard_outside'  // Guard is outside 50m radius
  | 'no_guard'       // Patrol has no active guard location
  | 'completed'      // Patrol is completed
  | 'pending';       // Patrol is scheduled but not started

/** Patrol location data for rendering circles on map */
export interface PatrolLocation {
  /** Schedule assignment ID */
  patrolId: number;
  /** Address ID for deduplication */
  addressId: number;
  /** Site/customer name */
  siteName: string;
  /** Customer name */
  customerName: string;
  /** Center latitude */
  latitude: number;
  /** Center longitude */
  longitude: number;
  /** Circle status for color coding */
  circleStatus: PatrolCircleStatus;
  /** Assigned guard ID if any */
  guardId: number | null;
  /** Assigned guard name */
  guardName: string | null;
  /** Distance from guard to site center in meters */
  guardDistance: number | null;
  /** Patrol status (pending/in_progress/completed) */
  patrolStatus: PatrolStatus;
}

/** Default radius for patrol circles in meters */
export const PATROL_CIRCLE_RADIUS_METERS = 50;

/** Patrol circle color configuration */
export interface PatrolCircleColor {
  fill: string;
  stroke: string;
  fillOpacity: number;
  strokeOpacity: number;
}

/** Patrol circle colors by status - values should match CSS variables in styles/index.css */
export const PATROL_CIRCLE_COLORS: Record<PatrolCircleStatus, PatrolCircleColor> = {
  guard_inside: {
    fill: '#4CAF50',       // --cl-success-light (green)
    stroke: '#4CAF50',
    fillOpacity: 0.2,
    strokeOpacity: 0.6,
  },
  guard_outside: {
    fill: '#FF9800',       // --cl-warning (orange)
    stroke: '#FF9800',
    fillOpacity: 0.2,
    strokeOpacity: 0.8,
  },
  no_guard: {
    fill: '#9E9E9E',       // --cl-grey-500 (grey)
    stroke: '#9E9E9E',
    fillOpacity: 0.15,
    strokeOpacity: 0.6,
  },
  completed: {
    fill: '#64b5f6',       // --cl-sds-main (blue)
    stroke: '#64b5f6',
    fillOpacity: 0.15,
    strokeOpacity: 0.4,
  },
  pending: {
    fill: '#9E9E9E',       // Same as no_guard (grey) for scheduled but not started
    stroke: '#9E9E9E',
    fillOpacity: 0.15,
    strokeOpacity: 0.6,
  },
};

/** Patrol circle status labels */
export const PATROL_CIRCLE_STATUS_LABELS: Record<PatrolCircleStatus, string> = {
  guard_inside: 'Guard On Site',
  guard_outside: 'Guard Away',
  no_guard: 'No Guard',
  completed: 'Completed',
  pending: 'Scheduled',
};

// ============================================================================
// Camera Types (VMS Camera Integration)
// ============================================================================

/** Camera status for VMS cameras on dispatch map */
export type CameraStatus = 'online' | 'offline' | 'unknown';

/** Camera stream type */
export type CameraStreamType = 'hls' | 'jpeg' | 'mjpeg';

/** Camera location data for dispatch map display */
export interface CameraLocation {
  id: number;
  name: string;
  display_name?: string;
  site_id: number;
  site_name: string;
  customer_id?: number | null;
  customer_name?: string | null;
  address_id?: number | null;
  address_title?: string | null;
  latitude: number;
  longitude: number;
  status: CameraStatus;
  stream_type: CameraStreamType;
  alert_count: number;
  ptz_capable?: boolean;
}

/** Properties attached to each camera point for supercluster */
export interface CameraPointProperties {
  cameraId: number;
  name: string;
  status: CameraStatus;
  hasAlerts: boolean;
}

/** A single camera point for supercluster (GeoJSON Feature) */
export type CameraPoint = Supercluster.PointFeature<CameraPointProperties>;

/** A camera cluster returned by supercluster */
export type CameraCluster = Supercluster.ClusterFeature<CameraPointProperties>;

/** Either a camera cluster or a single camera point */
export type CameraClusterOrPoint = CameraCluster | CameraPoint;

/** Type guard to check if a camera feature is a cluster */
export function isCameraCluster(feature: CameraClusterOrPoint): feature is CameraCluster {
  return 'cluster' in feature.properties && feature.properties.cluster === true;
}

/** Camera status colors */
export const CAMERA_STATUS_COLORS: Record<CameraStatus, string> = {
  online: '#4CAF50',   // Green
  offline: '#9E9E9E',  // Grey
  unknown: '#FFC107',  // Amber
};

/** Camera status labels */
export const CAMERA_STATUS_LABELS: Record<CameraStatus, string> = {
  online: 'Online',
  offline: 'Offline',
  unknown: 'Unknown',
};
